"use client";

import { useUser } from "@clerk/nextjs";
import {
  ArrowRightIcon,
  BoltIcon,
  CheckIcon,
  ClockIcon,
  PercentBadgeIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState, useEffect } from "react";
import { useLocalStorage } from "usehooks-ts";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { useSubscriptionDiscount } from "@/queries/use-subscription-discount";
import { usePostSubscriptionDiscount } from "@/queries/mutations/use-post-subscription-discount";

import { getUserPlanByMetadata } from "@/helper/user/client";
import { formatBRL } from "@/helper/currency";

import { cn } from "@/lib/utils";

import {
  billingPeriod,
  discountCoupon,
  plans,
  priceApp,
  storageKeys,
} from "@/constants";
import { pricingSearchParam } from "@/filters";

interface SubscriptionDiscountBannerProps {
  alwaysVisible?: boolean;
  rounded?: boolean;
  hideInSubscriptionPage?: boolean;
}

export const SubscriptionDiscountBanner = ({
  alwaysVisible,
  rounded,
  hideInSubscriptionPage,
}: SubscriptionDiscountBannerProps) => {
  const { user, isSignedIn } = useUser();
  const { hasPaidPlan } = getUserPlanByMetadata(user?.publicMetadata);
  const pathname = usePathname();

  const isCheckoutPage = pathname?.includes("/checkout");
  const isSubscriptionPage = pathname?.includes("/assinatura");

  const hiddenInSubscriptionPage = isSubscriptionPage && hideInSubscriptionPage;
  const canFetchDiscount =
    isSignedIn && !hasPaidPlan && !isCheckoutPage && !hiddenInSubscriptionPage;
  const { data, isPending, isError } =
    useSubscriptionDiscount(canFetchDiscount);
  const [isTrialBannerClosed, setIsTrialBannerClosed] = useLocalStorage(
    storageKeys.trialBannerClosed,
    false,
    { initializeWithValue: false }
  );
  const [isNormalBannerClosed, setIsNormalBannerClosed] = useLocalStorage(
    storageKeys.normalBannerClosed,
    false,
    { initializeWithValue: false }
  );
  const [isVisible, setIsVisible] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { mutate, isPending: isApplyingDiscount } =
    usePostSubscriptionDiscount();

  useEffect(() => {
    if (!data) return;

    if (data.trial) {
      setIsVisible(!isTrialBannerClosed);
    } else {
      setIsVisible(!isNormalBannerClosed);
    }
  }, [isTrialBannerClosed, isNormalBannerClosed, data]);

  const close = () => {
    if (data?.trial) {
      setIsTrialBannerClosed(true);
    } else {
      setIsNormalBannerClosed(true);
    }
    setIsVisible(false);
  };

  const openTrialDialog = () => {
    setIsDialogOpen(true);
  };

  const applyTrialDiscount = () => {
    if (data?.id) {
      mutate(
        {
          id: data.id,
          cupom: discountCoupon,
        },
        {
          onSuccess: () => {
            setIsDialogOpen(false);
            setIsTrialBannerClosed(true);
            setIsVisible(false);
          },
        }
      );
    }
  };

  if (
    hiddenInSubscriptionPage ||
    isCheckoutPage ||
    hasPaidPlan ||
    isPending ||
    isError
  )
    return null;

  if (!data) return null;

  const { mostrar_banner, trial, dias_restantes } = data;

  if (!mostrar_banner) return null;

  if (!alwaysVisible) {
    if (trial && isTrialBannerClosed) return null;
    if (!trial && isNormalBannerClosed) return null;
    if (!isVisible) return null;
  }

  if (trial) {
    const discount = priceApp * 0.15;

    return (
      <>
        <div
          className={cn(
            "relative bg-gradient-to-r from-green-50 via-emerald-100 to-lime-50 shadow-sm",
            rounded && "rounded-lg"
          )}
        >
          <div className="container mx-auto px-4 py-4 flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-4 mb-3 md:mb-0">
              <div className="bg-emerald-500 text-white p-2 rounded-full">
                <ClockIcon className="h-5 w-5" aria-hidden="true" />
              </div>
              <div>
                <p className="font-bold text-gray-800 text-lg">
                  Economize agora mesmo!
                </p>
                <p className="text-gray-700">
                  Seu período de teste termina em{" "}
                  <span className="font-bold text-emerald-600">
                    {dias_restantes} dias
                  </span>
                </p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
              <div className="flex items-center gap-2 bg-lime-100 px-3 py-1 rounded-full">
                <PercentBadgeIcon
                  className="h-5 w-5 text-lime-600"
                  aria-hidden="true"
                />
                <p className="font-bold text-lime-800">15% de desconto</p>
              </div>
              <div className="text-center md:text-left">
                <p className="text-gray-800 font-medium">
                  Nos primeiros 3 meses de assinatura
                </p>
              </div>
              <Button
                onClick={openTrialDialog}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-md transition-all transform hover:scale-105 flex items-center"
              >
                Aproveitar oferta
                <ArrowRightIcon className="ml-2 h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
            {!alwaysVisible ? (
              <button
                onClick={close}
                className="absolute right-3 top-3 text-gray-500 hover:text-gray-700 bg-white/80 rounded-full p-1"
                aria-label="Fechar"
              >
                <XMarkIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            ) : null}
          </div>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="sm:max-w-md bg-white">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-center text-gray-800">
                Aproveite a oferta!
              </DialogTitle>
              <DialogDescription className="text-center text-gray-600">
                Plano Especialista Mensal
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col space-y-4 my-4">
              <div className="flex justify-between items-center border-b pb-4">
                <p className="text-lg font-medium">5 dias grátis</p>
                <p className="text-base text-gray-600">
                  {formatBRL(priceApp)} / mês após o teste
                </p>
              </div>
              <div className="flex justify-between items-center border-b pb-4">
                <p className="font-medium">Subtotal</p>
                <p className="font-bold text-base">{formatBRL(priceApp)}</p>
              </div>
              <div className="flex justify-between items-center bg-blue-50 p-3 rounded-lg">
                <div className="flex items-center">
                  <PercentBadgeIcon className="h-5 w-5 text-blue-600 mr-2" />
                  <p className="font-medium">DESCONTO15POR3M</p>
                </div>
                <p className="text-blue-600 font-medium">-{discount}</p>
              </div>
              <p className="text-sm text-gray-600">
                15% de desconto nos próximos 3 ciclos de cobrança
              </p>
              <div className="flex justify-between items-center pt-8">
                <p className="font-medium text-lg">Total com desconto</p>
                <p className="font-bold text-2xl text-blue-700">
                  {formatBRL(priceApp - discount)}
                </p>
              </div>
            </div>
            <DialogFooter className="sm:justify-between flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className="sm:w-1/2"
              >
                Cancelar
              </Button>
              <Button
                onClick={applyTrialDiscount}
                disabled={isApplyingDiscount}
                className="bg-emerald-600 hover:bg-emerald-700 text-white sm:w-1/2"
              >
                {isApplyingDiscount ? "Aplicando..." : "Confirmar desconto"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <div
      className={cn(
        "relative bg-gradient-to-r from-emerald-50 via-green-100 to-teal-50 shadow-sm",
        rounded && "rounded-lg"
      )}
    >
      <div className="container mx-auto px-4 py-4 flex flex-col md:flex-row items-center justify-between">
        <div className="flex items-center space-x-4 mb-3 md:mb-0">
          <div className="bg-emerald-500 text-white p-2 rounded-full">
            <BoltIcon className="h-5 w-5" />
          </div>
          <div>
            <p className="font-bold text-gray-800 text-lg">
              Desbloqueie todos os recursos
            </p>
            <p className="text-gray-700">
              Assine agora com{" "}
              <span className="font-bold text-emerald-600">
                15% de desconto
              </span>{" "}
              nos primeiros 3 meses
            </p>
          </div>
        </div>
        <div className="hidden md:flex flex-row items-center gap-3 mx-4">
          <div className="flex flex-col gap-1">
            <div className="flex items-center text-sm text-gray-700">
              <CheckIcon className="h-4 w-4 text-emerald-500 mr-1" />
              <span>Busca avançada</span>
            </div>
            <div className="flex items-center text-sm text-gray-700">
              <CheckIcon className="h-4 w-4 text-emerald-500 mr-1" />
              <span>Alertas em tempo real</span>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <div className="flex items-center text-sm text-gray-700">
              <CheckIcon className="h-4 w-4 text-emerald-500 mr-1" />
              <span>Análises inteligente</span>
            </div>
            <div className="flex items-center text-sm text-gray-700">
              <CheckIcon className="h-4 w-4 text-emerald-500 mr-1" />
              <span>Suporte prioritário e muito mais</span>
            </div>
          </div>
        </div>
        <Button
          asChild
          className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-md transition-all transform hover:scale-105 flex items-center"
        >
          <Link
            prefetch={false}
            href={`/checkout?${pricingSearchParam.plano}=${plans.especialista.value}&${pricingSearchParam.periodo}=${billingPeriod.mensal}&${pricingSearchParam.cupom}=${discountCoupon}`}
            target="_blank"
          >
            Aproveitar oferta
            <ArrowRightIcon className="ml-2 h-4 w-4" aria-hidden="true" />
          </Link>
        </Button>
        {!alwaysVisible ? (
          <button
            onClick={close}
            className="absolute right-3 top-3 text-gray-500 hover:text-gray-700 bg-white/80 rounded-full p-1"
            aria-label="Fechar"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    </div>
  );
};
